import React from 'react'
import {
  makeStyles,
  Button,
  ActionButton,
  ConfirmationDialog,
  useDialogs,
  useUserPermissions,
  Paper,
  SelectableChipSmall
} from '@paintscout/ui'
import type { MuiTheme as Theme, QuoteRow } from '@paintscout/ui'
import { useSnackbar } from 'notistack'
import { useQuoteMutations } from 'shared/hooks'
import { Sentry } from 'src/util/appSentry'
import AddIcon from '@material-ui/icons/Add'
import { goToQuote } from 'src/util/routing'

const TAB_INVOICES = 'tab-invoices'

const useStyles = makeStyles<Theme>(
  (theme) => ({
    root: {},
    quoteContainerHeader: {
      display: 'flex',
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      borderBottom: `1px solid ${theme.palette.grey[300]} `
    },
    estimateButtonDiv: {
      display: 'flex',
      gap: theme.spacing(2),
      padding: theme.spacing(2)
    },
    estimateActions: {
      paddingRight: theme.spacing(1.5)
    },
    quoteContainerBody: {}
  }),
  { name: 'QuoteDocCard' }
)

export interface QuoteDocCardProps {
  children: React.ReactNode
  quote?: QuoteRow | null
  disabled: boolean
  label: string
  estimatesTab: string
  hasInvoice?: boolean
  onRemove: (id: string) => void
  onDelete: (ids: [string]) => void
  onCreate: () => void
  onChangeTab: (tab: React.SetStateAction<'tab-estimates' | 'tab-invoices'>) => void
  loading?: boolean
}

function QuoteDocCard({
  children,
  quote,
  estimatesTab,
  onChangeTab,
  disabled,
  hasInvoice,
  label,
  onCreate,
  onRemove,
  onDelete,
  loading = false
}: QuoteDocCardProps) {
  const { deleteQuotes, archiveQuotes } = useQuoteMutations()
  const { openDialog, dismissDialog } = useDialogs()
  const { enqueueSnackbar } = useSnackbar()
  const classes = useStyles()

  const {
    permissionToEditAssignedInvoice,
    permissionToEditAssignedQuote,
    permissionToEditAllInvoice,
    permissionToEditAllQuote
  } = useUserPermissions()
  const actions = {
    remove: onRemove ? true : false,
    edit: true,
    archive: true,
    delete: true
  }
  const quoteAction = (quote: QuoteRow) => {
    if (!quote) return []
    const canWriteAllType = quote.invoice ? permissionToEditAllInvoice : permissionToEditAllQuote
    const canWriteAssignedType = quote.invoice ? permissionToEditAssignedInvoice : permissionToEditAssignedQuote
    const allowedActions = []
    if (actions.edit && (quote.isOwner || canWriteAllType || (quote.isAssigned && canWriteAssignedType))) {
      allowedActions.push({
        key: 'edit',
        label: 'Edit'
      })
    }

    if (actions.archive && (quote.isOwner || canWriteAllType || (quote.isAssigned && canWriteAssignedType))) {
      allowedActions.push({
        key: 'archive',
        label: 'Archive'
      })
    }

    if (actions.delete && (quote.isOwner || canWriteAllType || (quote.isAssigned && canWriteAssignedType))) {
      allowedActions.push({
        key: 'delete',
        label: 'Delete'
      })
    }

    if (actions.remove && (quote.isOwner || canWriteAllType || (quote.isAssigned && canWriteAssignedType))) {
      allowedActions.push({
        key: 'remove',
        label: 'Remove Association'
      })
    }
    if (allowedActions.length > 0) {
      return allowedActions
    } else {
      return []
    }
  }

  const quoteActions = quoteAction(quote)

  const handleAction = (action: string, quoteRow: QuoteRow) => {
    const id = quoteRow._id
    console.log('action', { action, id })
    switch (action) {
      case 'edit':
        console.log('editing')
        goToQuote({ id })
        break
      case 'remove':
        console.log('removing')
        if (onRemove && id) {
          onRemove(id)
        }
        break

      case 'delete':
        openDialog(ConfirmationDialog, {
          message: `Warning! This ${label.toLowerCase()} will no longer be accessible and you cannot undo this action.`,
          scary: true,
          onConfirm: async () => {
            await deleteQuotes([id], true).catch((error) => {
              Sentry.captureException(error)
              enqueueSnackbar('Unable to delete', { variant: 'error' })
            })

            if (onDelete) {
              onDelete([id])
            }

            dismissDialog()
          },
          onCancel: dismissDialog
        })

        break

      case 'archive':
      case 'unarchive': {
        const unarchive = action === 'unarchive'

        openDialog(ConfirmationDialog, {
          message: (
            <div>
              <p>
                This will {action} the selected {label}.{' '}
                {!unarchive && (
                  <>
                    Archived {label}s <strong>are not available offline!</strong>
                  </>
                )}
              </p>
            </div>
          ),
          onConfirm: async () => {
            archiveQuotes([id], { unarchive }).catch((error) => {
              Sentry.captureException(error)
              enqueueSnackbar(`Unable to ${unarchive ? 'unarchive' : 'archive'}`, { variant: 'error' })
            })

            // if (onArchive) {
            //   onArchive(ids, unarchive)
            // }

            dismissDialog()
          },
          onCancel: dismissDialog
        })

        break
      }
    }
  }

  return (
    <Paper>
      <div className={classes.quoteContainerHeader}>
        <div className={classes.estimateButtonDiv}>
          <SelectableChipSmall
            label={label}
            selected={estimatesTab === 'tab-estimates'}
            onClick={() => onChangeTab('tab-estimates')}
          />
          <SelectableChipSmall
            label="Invoice"
            selected={estimatesTab === TAB_INVOICES}
            onClick={() => onChangeTab(TAB_INVOICES)}
          />
        </div>
        {!quote ? (
          estimatesTab === TAB_INVOICES ? (
            <></>
          ) : (
            <Button
              variant="hoverUnderline"
              color="primary"
              icon={AddIcon}
              onClick={onCreate}
              disabled={disabled || loading}
              loading={loading}
              className="tour-quote-table-new-button"
            >
              New
            </Button>
          )
        ) : estimatesTab === TAB_INVOICES ? (
          hasInvoice ? (
            <></>
          ) : (
            <Button
              variant="hoverUnderline"
              color="primary"
              icon={AddIcon}
              disabled={disabled || loading}
              loading={loading}
              onClick={onCreate}
              className="tour-quote-table-new-button"
            >
              New
            </Button>
          )
        ) : (
          <div className={classes.estimateActions}>
            <ActionButton
              data-testid="actions"
              actions={quoteActions}
              disabled={disabled || loading}
              onActionClick={(ev, action) => handleAction(action, quote)}
            />
          </div>
        )}
      </div>
      <div className={classes.quoteContainerBody}>{children}</div>
    </Paper>
  )
}

export default QuoteDocCard
